import useUpdatedRef from '@restart/hooks/useUpdatedRef';
import { useCallback, useMemo } from 'react';
import { useMutation } from 'react-relay-mutation';
// This doesn't extend Error because the stack trace isn't relevant here.
export class RelayMutationError {
  // Required to satisfy structural type for Error.
  constructor(payload) {
    this.name = 'RelayMutationError';
    const {
      __typename: errorType,
      message,
      ...data
    } = payload;
    this.errorType = errorType;
    this.message = message;
    this.data = data;
  }

}

function getErrorPayload(data) {
  const payload = data && Object.values(data)[0]; // FIXME: Payload type names end in "Payload". There might be better ways to
  //  do this with fragments and aliases but Relay doesn't handle that
  //  correctly for complex queries.
  // eslint-disable-next-line no-underscore-dangle

  if (!payload || payload.__typename.endsWith('Payload')) {
    // There was no error.
    return null;
  }

  return payload;
}

export function isMutationError(err) {
  return err.name === 'RelayMutationError';
}
export default function useRelayMutation(mutation, baseConfig = {}) {
  const [baseMutate, baseMutationState] = useMutation(mutation);
  const baseConfigRef = useUpdatedRef(baseConfig);
  const mutate = useCallback(config => {
    const {
      updater,
      onCompleted,
      onError,
      input = {},
      extraVariables,
      ...mergedConfig
    } = { ...baseConfigRef.current,
      ...config
    };
    return new Promise((resolve, reject) => {
      function handleError(error) {
        if (onError) {
          onError(error);
          resolve();
        } else {
          reject(error);
        }
      }

      baseMutate({ ...mergedConfig,
        variables: {
          input,
          ...extraVariables
        },
        onCompleted: response => {
          const errorPayload = getErrorPayload(response);

          if (errorPayload) {
            handleError(new RelayMutationError(errorPayload));
            return;
          }

          if (onCompleted) {
            onCompleted(response);
          }

          resolve(response);
        },
        onError: handleError,
        updater: updater && ((store, updaterData) => {
          if (getErrorPayload(updaterData)) {
            return;
          }

          updater(store, updaterData);
        })
      });
    });
  }, [baseConfigRef, baseMutate]);
  const mutationState = useMemo(() => {
    const {
      data
    } = baseMutationState;

    if (data) {
      const errorPayload = getErrorPayload(data);

      if (errorPayload) {
        return { ...baseMutationState,
          data: null,
          error: new RelayMutationError(errorPayload)
        };
      }
    }

    return baseMutationState;
  }, [baseMutationState]);
  return [mutate, mutationState];
}